import React, { useEffect, useRef, useState } from "react";

import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router";

import {
  default as Firelap,
  default as FlashcardsAss,
} from "~/ass/flashcards.json";
import LearnAss from "~/ass/learn.json";
import PhraseAss from "~/ass/phrase.json";
import QuizAss from "~/ass/quiz.json";
import AnimatedSticker from "~/components/animated-stickers";
import Typography from "~/components/typography";
import { Button } from "~/components/ui/button";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "~/components/ui/carousel";
import { useGenFlashcards, useGenFlashcardsState } from "~/lib/hooks/api.hooks";

const CarouselSection = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  return (
    <div className="flex flex-col">
      <Carousel setApi={setApi} className="my-auto w-full">
        <CarouselContent>
          <CarouselItem>
            <AnimatedSticker
              title="Language Learning AI"
              desc="Learn new languages with AI-driven flashcards, quizzes, and interactive learning. Pick up a book/movie/lyric/etc and start reading it with Lezan."
              data={{ raw: Firelap }}
            />
          </CarouselItem>
          <CarouselItem>
            <AnimatedSticker
              title="Phrase"
              desc="Enter a phrase in your target language to begin your learning journey."
              data={{ raw: PhraseAss }}
            />
          </CarouselItem>
          <CarouselItem>
            <AnimatedSticker
              title="Learn"
              desc="Review flashcards generated from your phrase to enhance your vocabulary."
              data={{ raw: LearnAss }}
            />
          </CarouselItem>
          <CarouselItem>
            <AnimatedSticker
              title="Quiz"
              desc="Evaluate your understanding with an AI-generated quiz tailored to your chosen phrase."
              data={{ raw: QuizAss }}
            />
          </CarouselItem>
        </CarouselContent>
      </Carousel>
      <Button
        onClick={() => {
          if (current === count) {
            api?.scrollTo(0);
          } else {
            api?.scrollNext();
          }
        }}
        variant="secondary"
        className="flex w-min mx-auto"
      >
        Learn More <ChevronRight />
      </Button>
    </div>
  );
};

const HomePage = () => {
  const genFlashcardsState = useGenFlashcardsState();

  if (genFlashcardsState[0]?.status === "pending") {
    return (
      <AnimatedSticker
        title="Cooking your phrase..."
        desc="Loading flashcards generated from your input phrase"
        data={{
          raw: FlashcardsAss,
        }}
      />
    );
  }

  return (
    <>
      <CarouselSection />
      <PhraseInput />
    </>
  );
};

const PhraseInput = () => {
  const [isTyping, setIsTyping] = useState(false);
  const [draft, setDraft] = useState("");
  const textRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const genFlashcards = useGenFlashcards();
  const navigate = useNavigate();

  useEffect(() => {
    const saved = localStorage.getItem("draftPhrase") || "";
    setDraft(saved);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "/") {
        event.preventDefault();
        if (document.activeElement === textRef.current) {
          textRef.current?.blur();
        } else {
          textRef.current?.focus();
        }
      }

      if (
        document.activeElement === textRef.current &&
        event.ctrlKey &&
        event.key === "Enter"
      ) {
        event.preventDefault();
        formRef.current?.requestSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const phrase = formData.get("phrase") as string;

    const res = await genFlashcards.mutateAsync({
      phrase,
    });

    navigate(`/app/flashcards/${res.id}`);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setDraft(value);
    localStorage.setItem("draftPhrase", value);
  };

  return (
    <div className="sm:my-auto pb-4 sm:pb-20 mt-auto w-full max-w-2xl ">
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="flex px-4 w-full flex-col items-center gap-2"
      >
        <Typography variant="h2">What phrase do you want to learn?</Typography>
        <textarea
          name="phrase"
          ref={textRef}
          value={draft}
          onChange={handleChange}
          onFocus={() => {
            setIsTyping(true);
          }}
          onBlur={() => setIsTyping(false)}
          autoComplete="on"
          placeholder="Enter a phrase to learn"
          className="font-semibold text-xl backdrop-blur-md w-full h-32 mt-4 px-4 py-2 rounded-xl border border-white/60 bg-white/10 placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white"
        />
        {(isTyping || draft) && (
          <Button
            disabled={genFlashcards.isPending}
            type="submit"
            className="w-full"
          >
            Learn
          </Button>
        )}
      </form>
    </div>
  );
};

export default HomePage;
